import React, { ReactNode, useEffect, useState } from 'react'

import Box from 'components_new/atoms/Box'
import Link from 'components_new/atoms/Link'
import LoadingSpinner from 'components_new/atoms/LoadingSpinner'
import Table from 'components_new/atoms/Table'
import TableBody from 'components_new/atoms/Table/TableBody'
import TableCell from 'components_new/atoms/Table/TableCell'
import TableHead from 'components_new/atoms/Table/TableHead'
import TableRow from 'components_new/atoms/Table/TableRow'
import TableSortLabel from 'components_new/atoms/Table/TableSortLabel'
import TableFooter from 'components_new/atoms/Table/TableFooter'
import Tag from 'components_new/atoms/Tag'
import Text from 'components_new/atoms/Text'

import { TableVirtuoso, TableComponents, TableBodyProps } from 'react-virtuoso'
import { Order, Sort } from 'utils/types'
import {
  ColumnSearch as ColumnSearchState,
  formatNumberValue
} from 'utils/underlyingData'
import { DatasetDataValue, DatasetType } from 'types/GlobalWidget'
import { parseDatasetValue, isNumberDatasetType } from 'utils/functions'
import { Highlighted } from 'components_new/organisms/StandardSearch/utils'
import { isSourceFileLink } from 'utils/sourceFiles'
import { useSourceFileDownload } from 'helpers/hooks/SourceFiles'
import { useTranslation } from 'language/translator'
import { SxProps } from '@mui/material'

import ColumnSearch, {
  COLUMN_SEARCH_REVEAL_SELECTOR,
  ColumnSearchSpacer,
  SORT_ARROW_FONT_SIZE
} from './ColumnSearch'


export type TTableRow = {
  segment: {
    label: string
    link?: string
  }
  values: {
    [columnId: string]: DatasetDataValue
  }
}

export interface TTableColumn {
  id: string
  index?: number
  hidden?: boolean
  label: string
  type?: DatasetType
  endAdornment?: ReactNode
}

function parseTableRowValue(
  dataValue: DatasetDataValue,
  dtype: DatasetType = DatasetType.STRING,
  query = ''
) {
  const value = parseDatasetValue(dataValue, dtype)

  if (isNumberDatasetType(dtype)) {
    const text = formatNumberValue(value)

    return text === null ? null : (
      <Highlighted highlight={query} ignoreWhitespace={true} text={text} />
    )
  } else if (dtype === DatasetType.ENUM) {
    return (
      <Box sx={{ display: 'flex', gridGap: 4, justifyContent: 'center' }}>
        {((value as string[] | null) ?? []).map((item, i) => (
          <Tag
            key={i}
            label={
              <Highlighted
                highlight={query}
                ignoreWhitespace={true}
                text={item}
              />
            }
          />
        ))}
      </Box>
    )
  }

  return (
    <Highlighted
      highlight={query}
      ignoreWhitespace={true}
      text={((value as string[] | null) ?? []).join(',')}
    />
  )
}

const VirtuosoTableComponents: TableComponents<TTableRow> = {
  Table: (props: any) => (
    <Table {...props} sx={{ borderCollapse: 'separate' }} size={'small'} />
  ),
  TableHead: React.forwardRef((props, ref) => (
    <TableHead {...props} ref={ref} />
  )),
  TableRow: (props: any) => <TableRow hover={true} {...props} />,
  TableBody: React.forwardRef<
    HTMLTableSectionElement,
    TableBodyProps & { context?: any }
  >(({ ...props }, ref) => <TableBody {...props} ref={ref} />),
  TableFoot: React.forwardRef((props, ref) => (
    <TableFooter {...props} ref={ref} style={{ position: 'relative' }} />
  ))
}

/**
 * Calculate width of column based on options and char length
 */
// const calculateWidth = (column: ColumnData) => {
//   const charactersWidth = column.label.length * 6
//   const sortIconWidth = 24
//   const filterIconWidth = column.filter ? 36 : 0

//   return charactersWidth + sortIconWidth + filterIconWidth
// }

/**
 * Get value alignment in column
 * @param dtype - DatasetType of column
 */
const getAligment = (dtype?: DatasetType) => {
  if (dtype === DatasetType.ENUM) {
    return 'center'
  }

  if (isNumberDatasetType(dtype) || dtype === DatasetType.DATE) {
    return 'right'
  }

  return 'left'
}

type HeaderSearch = {
  onOpen: (columnId: string | null) => void
  onSearch: (columnId: string, query: string) => void
  openColumnId: string | null
  search: ColumnSearchState
}

function fixedHeaderContent(
  columns: TTableColumn[],
  sort: Sort | null,
  onSort?: (column: string) => void,
  headerSearch?: HeaderSearch
) {
  return (
    <TableRow>
      {columns.map((column, i) => {
        if (column.hidden) {
          return null
        }

        const active = column.id === sort?.attribute
        const direction = sort?.attribute === column.id ? sort.direction : 'asc'
        const align = getAligment(column.type)
        // A right aligned column lays its header out backwards, so the label
        // stays flush with the values below it and the controls sit inside.
        const reversed = align === 'right'

        const search = headerSearch ? (
          <ColumnSearch
            label={column.label}
            onChange={(value) => headerSearch.onSearch(column.id, value)}
            onClose={() => headerSearch.onOpen(null)}
            onOpen={() => headerSearch.onOpen(column.id)}
            open={headerSearch.openColumnId === column.id}
            placement={reversed ? 'start' : 'end'}
            query={headerSearch.search[column.id] ?? ''}
          />
        ) : null

        return (
          <TableCell
            key={column.id}
            variant={'head'}
            align={align}
            sx={{
              bgcolor: 'background.paper',
              whiteSpace: 'nowrap',
              width: '1%', // For auto sized, fit content cells. (Hacker).
              ...(i === 0
                ? {
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.default'
                  }
                : null),
              [COLUMN_SEARCH_REVEAL_SELECTOR]: { opacity: 1 }
            }}
            sortDirection={direction}
          >
            {/*
             * Two groups spaced apart: the label against the column's own
             * edge, the search trigger against the far one. A centered column
             * adds a matching spacer so its label stays in the middle.
             */}
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: reversed ? 'row-reverse' : 'row',
                justifyContent: 'space-between'
              }}
            >
              {align === 'center' && search ? <ColumnSearchSpacer /> : null}

              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'inherit',
                  gap: onSort ? 0 : 0.5
                }}
              >
                {onSort ? (
                  <TableSortLabel
                    onClick={() => onSort(column.id)}
                    active={active}
                    direction={direction}
                    sx={{
                      fontSize: 'inherit',
                      '& svg': {
                        fontSize: SORT_ARROW_FONT_SIZE,
                        // Out of flow while inactive, so a hovered arrow never
                        // widens the column. It floats on the label's outer
                        // side, where the trigger keeps its clearance.
                        ...(!active
                          ? {
                              position: 'absolute',
                              ...(reversed ? { left: -20 } : { right: -20 })
                            }
                          : null)
                      }
                    }}
                  >
                    {column.label}
                  </TableSortLabel>
                ) : (
                  column.label
                )}
                {column.endAdornment}
              </Box>

              {search}
            </Box>
          </TableCell>
        )
      })}
    </TableRow>
  )
}

const SegmentLink = (props: {
  link: string
  label: ReactNode
  onLinkClick?: () => void
}) => {
  const { link, label, onLinkClick } = props

  const { download, loading, error } = useSourceFileDownload()
  const { t } = useTranslation()

  // source file links are resolved through the backend and downloaded
  // instead of being opened directly
  if (isSourceFileLink(link)) {
    return (
      <Box
        component="span"
        sx={{ alignItems: 'center', display: 'inline-flex', gap: 0.5 }}
      >
        <Link
          onClick={() => {
            download(link)
            onLinkClick?.()
          }}
        >
          {label}
        </Link>
        {loading ? <LoadingSpinner size={12} /> : null}
        {error ? (
          <Text color="error.main" component="span" variant="caption">
            {t('global', 'something_went_wrong')}
          </Text>
        ) : null}
      </Box>
    )
  }

  return (
    <Link href={link} newTab={true} onClick={onLinkClick}>
      {label}
    </Link>
  )
}

function rowContent(
  _index: number,
  row: TTableRow,
  columns: TTableColumn[],
  onLinkClick?: () => void,
  search?: ColumnSearchState
) {
  return (
    <React.Fragment>
      {columns.map((column, i) => {
        if (column.hidden) {
          return null
        }

        const query = search?.[column.id] ?? ''

        return (
          <TableCell
            key={column.id}
            align={getAligment(column.type)}
            sx={{
              ...(i === 0
                ? {
                    position: 'sticky',
                    left: 0,
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.default'
                  }
                : null),
              whiteSpace: 'nowrap',
              width: '1%' // For auto sized, fit content cells. (Hacker).
            }}
          >
            {i === 0 ? (
              <Text component="span" fontWeight="medium" variant="body2">
                {row.segment.link ? (
                  <SegmentLink
                    link={row.segment.link}
                    label={
                      <Highlighted
                        highlight={query}
                        ignoreWhitespace={true}
                        text={row.segment.label}
                      />
                    }
                    onLinkClick={onLinkClick}
                  />
                ) : (
                  <Highlighted
                    highlight={query}
                    ignoreWhitespace={true}
                    text={row.segment.label}
                  />
                )}
              </Text>
            ) : (
              parseTableRowValue(row.values[column.id], column.type, query)
            )}
          </TableCell>
        )
      })}
    </React.Fragment>
  )
}

interface SegmentVirtualizedTableProps {
  data: TTableRow[]
  columns: TTableColumn[]
  loading: boolean
  sort?: Sort | null
  onSort?: (value: Sort) => void
  onLinkClick?: () => void
  // Per-column free-text search. Providing onSearch enables the search
  // trigger in every column header; an empty query clears that column.
  search?: ColumnSearchState
  onSearch?: (columnId: string, query: string) => void
  sx?: SxProps
}

// @TODO: Change this to be on the data format we use in the rest of the app:
// data: {
//   value: (null | number | string)[]
//   id: string
//   index: number
//   label: string
//   type: 'date' | 'number' | 'text'
// }[]
// /* ... */
// segments: {
//   labels: string[]
//   links?: string[]
//   title: string
// }

const SegmentVirtualizedTable = (props: SegmentVirtualizedTableProps) => {
  const {
    columns,
    data,
    loading,
    sort,
    onSort,
    onLinkClick,
    search,
    onSearch,
    sx
  } = props

  const orderBy = sort?.attribute || null
  const order = sort?.direction ?? 'asc'

  // The column whose search popover is open.
  const [openColumnId, setOpenColumnId] = useState<string | null>(null)

  // Close the popover if its column disappears (view switch, columns edited).
  useEffect(() => {
    if (openColumnId && !columns.some((column) => column.id === openColumnId)) {
      setOpenColumnId(null)
    }
  }, [columns, openColumnId])

  const headerSearch: HeaderSearch | undefined = onSearch
    ? {
        onOpen: setOpenColumnId,
        onSearch,
        openColumnId,
        search: search ?? {}
      }
    : undefined

  function sortHandler(id: string) {
    if (!onSort || !id || loading) {
      return
    }
    let newOrder: Order = 'asc'
    let newOrderBy = !orderBy ? id : orderBy

    if (orderBy === id) {
      // order changed
      newOrder = order === 'asc' ? 'desc' : 'asc'
    } else {
      newOrderBy = id
    }

    onSort({
      attribute: newOrderBy,
      direction: newOrder
    })
  }

  return (
    <Box sx={sx}>
      <TableVirtuoso
        data={data}
        components={VirtuosoTableComponents}
        fixedHeaderContent={() =>
          fixedHeaderContent(columns, sort ?? null, sortHandler, headerSearch)
        }
        itemContent={(index: number, row: TTableRow) =>
          rowContent(index, row, columns, onLinkClick, search)
        }
        overscan={200}
      />
    </Box>
  )
}

SegmentVirtualizedTable.displayName = 'SegmentVirtualizedTable'
export default SegmentVirtualizedTable
